/**
 * José de Faktelio: el que acompaña la cualificación de una oportunidad —
 * `OD-0058-ENMIENDA-01` sobre `OD-0058` (Business).
 *
 * ```text
 * E-02  un consejo por cada respuesta que no es la favorable; diez o más por
 *       pregunta y por caso ("No" y "No lo sé"), que van rotando
 * E-03  la lectura del porcentaje: una frase según el tramo y la pregunta que
 *       más lo sube (esa la calcula la API: `lever`)
 * E-04  de una en una: la siguiente pregunta sin responder, y al terminar un
 *       resumen en una frase
 * E-05  el siguiente paso: una acción concreta que se apunta como tarea
 * ```
 *
 * Y la ENMIENDA-02 (2026-10-29): «Sí» es siempre lo bueno (G-04), con menos de
 * tres respuestas el porcentaje es el de la etapa (G-03), debajo del número se
 * dice qué lo sube y qué lo baja (G-06), y la pista de José —lo que más lo
 * subiría— va arriba, con «Recordármelo» (G-07).
 *
 * Aquí solo hay REGLAS, y devuelven claves: los textos viven en `i18n/es.ts`
 * (`t.jose`), como todos los de la web. Nada de IA ni de azar: el consejo
 * que toca sale de la oportunidad y del día, así que dos personas que miran la
 * misma ficha el mismo día ven el mismo, y mañana ven otro.
 *
 * Ninguna cuenta con dinero, y ninguna conversión a número, que la web tiene
 * prohibido (`tests/adversarial/web-shell.mjs`). El porcentaje llega ya
 * calculado por la API.
 */

export type Pregunta = "budget" | "authority" | "timing" | "competition" | "trust";
export type Respuesta = "YES" | "NO" | "UNKNOWN";

/** En el orden de la ficha. Es el de las preguntas de una en una (E-04). */
export const PREGUNTAS: readonly Pregunta[] = ["budget", "authority", "timing", "competition", "trust"];

/**
 * ENMIENDA-02 G-04: lo favorable es «Sí» en las cinco. `competition` pregunta
 * «¿Eres el único al que ha pedido presupuesto?».
 */
export const FAVORABLE: Readonly<Record<Pregunta, Respuesta>> = { budget: "YES", authority: "YES", timing: "YES", competition: "YES", trust: "YES" };

/** G-03: con menos, el porcentaje es el de la etapa. La API tiene el mismo (el test lo compara). */
export const MINIMO_RESPUESTAS = 3;

/**
 * Qué riesgo se dice primero en el resumen: el dinero y quién decide pesan más
 * que el resto en un negocio pequeño, y la competencia antes que el plazo.
 */
const PRIORIDAD: readonly Pregunta[] = ["budget", "authority", "competition", "timing", "trust"];

type Cualificacion = Readonly<Record<Pregunta, Respuesta | null>>;

/** El caso de un consejo: la respuesta en contra, o no saberlo. */
export type Caso = "desfavorable" | "noLoSe";

/** `null` si la respuesta es la favorable o no hay respuesta: no hay consejo que dar. */
export function casoDe(pregunta: Pregunta, respuesta: Respuesta | null): Caso | null {
  if (respuesta === null || respuesta === FAVORABLE[pregunta]) return null;
  return respuesta === "UNKNOWN" ? "noLoSe" : "desfavorable";
}

/** Día local como entero: el consejo cambia cuando cambia el día del usuario. */
export function diaLocal(ahora: Date): number {
  const inicio = Date.UTC(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  return Math.floor(inicio / 86_400_000);
}

/** FNV-1a de 32 bits: estable entre navegadores, sin dependencias. */
function huella(texto: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * E-02: qué consejo de los `total` toca. Cada oportunidad y pregunta empieza en
 * un sitio distinto, avanza uno cada día y `otro` avanza a mano ("Otro
 * consejo"). Siempre un índice válido.
 */
export function indiceConsejo(p: {
  readonly oportunidadId: string;
  readonly pregunta: Pregunta;
  readonly caso: Caso;
  readonly dia: number;
  readonly otro: number;
  readonly total: number;
}): number {
  if (p.total <= 0) return 0;
  const base = huella(`${p.oportunidadId}|${p.pregunta}|${p.caso}`) % p.total;
  const paso = (((p.dia + p.otro) % p.total) + p.total) % p.total;
  return (base + paso) % p.total;
}

/** E-04: la siguiente sin responder, saltando las que el usuario dejó para luego. */
export function siguientePregunta(q: Cualificacion, saltadas: ReadonlySet<Pregunta>): Pregunta | null {
  return PREGUNTAS.find((p) => q[p] === null && !saltadas.has(p)) ?? null;
}

/** E-03: el tramo que lee José. Los cortes son los de la etiqueta de color (OD-0058 D-06). */
export type Tramo = "sinDatos" | "dificil" | "enElAire" | "buenaPinta" | "casiHecha";

export function tramoDe(probabilidad: number | null): Tramo {
  if (probabilidad === null) return "sinDatos";
  if (probabilidad < 35) return "dificil";
  if (probabilidad < 65) return "enElAire";
  if (probabilidad < 90) return "buenaPinta";
  return "casiHecha";
}

/**
 * E-04: el resumen al terminar. El tramo y el riesgo que más pesa: primero una
 * respuesta en contra, después algo que no se sabe. Sin riesgo, `null`: lo
 * tiene todo a favor. `pero` si el tramo es bueno ("Buena pinta, pero…").
 */
export function resumen(
  q: Cualificacion,
  probabilidad: number | null,
): { readonly tramo: Tramo; readonly riesgo: { readonly pregunta: Pregunta; readonly caso: Caso } | null; readonly pero: boolean } {
  const tramo = tramoDe(probabilidad);
  const buscar = (caso: Caso) => PRIORIDAD.find((p) => casoDe(p, q[p]) === caso);
  const contra = buscar("desfavorable");
  const duda = buscar("noLoSe");
  const riesgo = contra !== undefined ? { pregunta: contra, caso: "desfavorable" as const } : duda !== undefined ? { pregunta: duda, caso: "noLoSe" as const } : null;
  return { tramo, riesgo, pero: probabilidad !== null && probabilidad >= 50 };
}

/** Todas respondidas: lo que pide el resumen. */
export function completa(q: Cualificacion): boolean {
  return PREGUNTAS.every((p) => q[p] !== null);
}

export function respondidas(q: Cualificacion): number {
  return PREGUNTAS.filter((p) => q[p] !== null).length;
}

/**
 * G-03: cómo se dice que una abierta no está cualificada todavía. Sin ninguna
 * respuesta, «Sin cualificar»; con una o dos, «Provisional». Las dos valen la
 * base de su etapa.
 */
export function estadoCualificacion(o: { readonly probabilitySource: string | null; readonly qualification: Cualificacion }): "sinCualificar" | "provisional" | null {
  if (o.probabilitySource !== "STAGE") return null;
  return respondidas(o.qualification) === 0 ? "sinCualificar" : "provisional";
}

/**
 * G-06: lo que explica el número, en el orden de la ficha: lo que lo sube (los
 * «Sí») y lo que lo baja (los «No»). «No lo sé» no mueve nada y no se nombra.
 */
export function factores(q: Cualificacion): { readonly sube: readonly Pregunta[]; readonly baja: readonly Pregunta[] } {
  return { sube: PREGUNTAS.filter((p) => q[p] === "YES"), baja: PREGUNTAS.filter((p) => q[p] === "NO") };
}

/** Los tipos de actividad que propone José (OD-0050). */
export type TipoPaso = "CALL" | "TASK" | "EMAIL";

/**
 * E-05: la acción. Una pregunta: `preguntar` (no se sabe) o `mejorar` (está en
 * contra). Sin pregunta que mover, según el presupuesto enlazado.
 */
export type ClavePaso =
  | { readonly tipo: "pregunta"; readonly pregunta: Pregunta; readonly accion: "preguntar" | "mejorar" }
  | { readonly tipo: "etapa"; readonly accion: "presupuesto" | "enviar" | "seguimiento" | "cerrar" | "recuperar" };

export type Paso = { readonly clave: ClavePaso; readonly kind: TipoPaso; readonly dias: number };

/** Qué tipo de actividad y para dentro de cuántos días. "Esta semana" = dos días. */
const PASO_PREGUNTA: Readonly<Record<Pregunta, Readonly<Record<"preguntar" | "mejorar", { readonly kind: TipoPaso; readonly dias: number }>>>> = {
  budget: { preguntar: { kind: "CALL", dias: 2 }, mejorar: { kind: "TASK", dias: 2 } },
  authority: { preguntar: { kind: "CALL", dias: 2 }, mejorar: { kind: "CALL", dias: 2 } },
  // Sin prisa: volver a llamar dentro de un mes, no esta semana.
  timing: { preguntar: { kind: "CALL", dias: 2 }, mejorar: { kind: "CALL", dias: 30 } },
  competition: { preguntar: { kind: "CALL", dias: 2 }, mejorar: { kind: "CALL", dias: 1 } },
  trust: { preguntar: { kind: "CALL", dias: 2 }, mejorar: { kind: "EMAIL", dias: 1 } },
};

const PASO_ETAPA: Readonly<Record<"presupuesto" | "enviar" | "seguimiento" | "cerrar" | "recuperar", { readonly kind: TipoPaso; readonly dias: number }>> = {
  presupuesto: { kind: "TASK", dias: 0 },
  enviar: { kind: "TASK", dias: 0 },
  seguimiento: { kind: "CALL", dias: 3 },
  cerrar: { kind: "TASK", dias: 2 },
  recuperar: { kind: "CALL", dias: 2 },
};

const PRESUPUESTO_ACABADO: readonly string[] = ["REJECTED", "EXPIRED", "CANCELLED"];

export function presupuestoAcabado(quoteStatus: string | null): boolean {
  return quoteStatus !== null && PRESUPUESTO_ACABADO.includes(quoteStatus);
}

/**
 * El paso sobre una pregunta: averiguarlo si no se sabe, mejorarlo si es «No».
 * Es el del siguiente paso (E-05) y el de «Recordármelo» en la pista (G-07).
 */
export function pasoDePregunta(pregunta: Pregunta, respuesta: Respuesta | null): Paso {
  const accion = respuesta === null || respuesta === "UNKNOWN" ? "preguntar" : "mejorar";
  return { clave: { tipo: "pregunta", pregunta, accion }, ...PASO_PREGUNTA[pregunta][accion] };
}

/**
 * E-05. Solo abierta y con tres respuestas (o fijada a mano). La pregunta que
 * se mueve es la que más sube el porcentaje (`lever`, de la API); sin ella, la
 * primera en contra o sin saber, por `PRIORIDAD`. Si todo está a favor, lo que
 * toque con el presupuesto.
 */
export function siguientePaso(o: {
  readonly status: string;
  readonly probability: number | null;
  readonly probabilitySource?: string | null;
  readonly qualification: Cualificacion;
  readonly lever: { readonly question: Pregunta } | null;
  readonly quote: { readonly status: string | null } | null;
}): Paso | null {
  // Sin cualificar (G-03) José todavía está preguntando: el paso viene después.
  if (o.status !== "OPEN" || o.probability === null || o.probabilitySource === "STAGE") return null;
  const q = o.qualification;
  const quoteStatus = o.quote?.status ?? null;
  // Con el presupuesto rechazado, caducado o cancelado no hay pregunta que
  // valga: toca saber por qué y si hay otra opción.
  if (presupuestoAcabado(quoteStatus)) return { clave: { tipo: "etapa", accion: "recuperar" }, ...PASO_ETAPA.recuperar };
  const pregunta =
    o.lever?.question ?? PRIORIDAD.find((p) => casoDe(p, q[p]) === "desfavorable") ?? PRIORIDAD.find((p) => q[p] !== FAVORABLE[p]) ?? null;
  if (pregunta !== null) return pasoDePregunta(pregunta, q[pregunta]);
  const accion = o.quote === null ? "presupuesto" : quoteStatus === "DRAFT" ? "enviar" : quoteStatus === "ACCEPTED" ? "cerrar" : "seguimiento";
  return { clave: { tipo: "etapa", accion }, ...PASO_ETAPA[accion] };
}

/**
 * E-07: el nombre de un paso para la API y las métricas, `authority.preguntar`
 * o `etapa.enviar`. La API tiene el mismo catálogo (`metricas-jose.ts`) y el
 * test comprueba que coinciden.
 */
export function clavePaso(c: ClavePaso): string {
  return c.tipo === "pregunta" ? `${c.pregunta}.${c.accion}` : `etapa.${c.accion}`;
}

/** E-07: todos los pasos que puede proponer José, de las mismas tablas de arriba. */
export const CLAVES_PASO: readonly string[] = [
  ...PREGUNTAS.flatMap((p) => Object.keys(PASO_PREGUNTA[p]).map((a) => `${p}.${a}`)),
  ...Object.keys(PASO_ETAPA).map((a) => `etapa.${a}`),
];

/**
 * E-07: el id de un consejo, `budget.noLoSe.03`: su número en la lista, desde
 * 1. Por eso los consejos nuevos van al FINAL y no se reordenan.
 */
export function idConsejo(pregunta: Pregunta, caso: Caso, indice: number): string {
  return `${pregunta}.${caso}.${String(indice + 1).padStart(2, "0")}`;
}

/** AAAA-MM-DD local dentro de `dias` días: la fecha de la tarea que se apunta. */
export function fechaEnDias(ahora: Date, dias: number): string {
  const d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
